import { useQuery } from "@tanstack/react-query";

import { buildRentalUpdateRequestBody } from "features/subscriptions/helpers/rentalSubscriptions";
import type { BillingEstimate } from "features/subscriptions/types";
import type { getCouponUpdateState } from "features/subscriptions/utils";

import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import getQueryKeys from "common/datahooks/getQueryKeys";
import { post } from "common/helpers/HTTP";

type CouponUpdateState = ReturnType<typeof getCouponUpdateState>;

async function rentalEstimateUpdateRequest(
  workspaceId: string,
  subscriptionId: string,
  {
    count,
    planPricingId,
    couponUpdateState,
  }: {
    count: number;
    planPricingId: string | undefined;
    couponUpdateState?: CouponUpdateState;
  },
): Promise<BillingEstimate> {
  const body = buildRentalUpdateRequestBody({
    count,
    planPricingId,
    couponUpdateState,
  });

  const { estimate } = await post(
    `workspaces/${workspaceId}/subscriptions/${subscriptionId}/rental/estimate`,
    body,
  );
  return estimate;
}

export default function useRentalEstimateUpdate({
  subscriptionId,
  count,
  planPricingId,
  couponUpdateState,
  isEnabled = true,
}: {
  subscriptionId: string;
  count: number;
  planPricingId: string | undefined;
  couponUpdateState?: CouponUpdateState;
  isEnabled?: boolean;
}) {
  const { id: workspaceId } = useSelectedWorkspaceContext();

  const {
    data: estimateUpdate,
    isFetching: isLoadingEstimateUpdate,
    error: estimateUpdateError,
  } = useQuery({
    queryKey: getQueryKeys(workspaceId).subscriptionKeys.rentalUpdateEstimate(
      subscriptionId,
      count,
      planPricingId,
      couponUpdateState,
    ),
    queryFn: () =>
      rentalEstimateUpdateRequest(workspaceId, subscriptionId, {
        count,
        planPricingId,
        couponUpdateState,
      }),
    enabled: isEnabled,
    gcTime: 0,
  });

  return { estimateUpdate, isLoadingEstimateUpdate, estimateUpdateError };
}
